/* eslint-disable @reatom/unit-naming-rule */
import {
  atom,
  onConnect,
  reaction,
  reatomAsync,
  sleep,
  withAbort,
} from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { reatomAsyncState } from '@root/shared/lib'

import { pickBestVariant } from '../services/pathfinder-swap-service'

import type {
  PathfinderRoutingMode,
  PathfinderSwapService,
  PathfinderError,
} from '../services/pathfinder-swap-service'
import type { QuoteResponseBody } from '@blade/pathfinder-api-client'
import type { Atom } from '@reatom/framework'

const INPUT_DEBOUNCE_MS = 250

export interface PathfinderQuoteInputs {
  serviceAtom: Atom<PathfinderSwapService | undefined>
  amountInRawAtom: Atom<bigint | undefined>
  tokenInAtom: Atom<string | undefined>
  tokenOutAtom: Atom<string | undefined>
  takerAtom: Atom<string | undefined>
  modeAtom?: Atom<PathfinderRoutingMode>
  name?: string
}

export const reatomPathfinderQuote = (inputs: PathfinderQuoteInputs) => {
  const name = inputs.name ?? 'pathfinder-quote'

  const quoteStateAtom = reatomAsyncState.notInitiated<
    PathfinderError,
    QuoteResponseBody
  >(`${name}.state`)

  const lastLatencyMsAtom = atom<number | undefined>(
    undefined,
    `${name}.lastLatencyMs`,
  )

  const refreshAction = reatomAsync(async (ctx) => {
    const service = ctx.get(inputs.serviceAtom)
    const amount = ctx.get(inputs.amountInRawAtom)
    const tokenIn = ctx.get(inputs.tokenInAtom)
    const tokenOut = ctx.get(inputs.tokenOutAtom)
    const taker = ctx.get(inputs.takerAtom)
    const mode = inputs.modeAtom ? ctx.get(inputs.modeAtom) : 'best'

    if (
      !service ||
      !tokenIn ||
      !tokenOut ||
      !taker ||
      amount === undefined ||
      amount <= 0n
    ) {
      quoteStateAtom.setNotInitiated(ctx)
      return
    }

    quoteStateAtom.setLoading(ctx)

    const t0 = performance.now()
    const result = await ctx.schedule(() =>
      service.quote({
        tokenIn,
        tokenOut,
        amountInRaw: amount,
        taker,
        mode,
      }),
    )
    lastLatencyMsAtom(ctx, performance.now() - t0)

    if (Result.isSuccess(result)) {
      quoteStateAtom.setSuccess(ctx, result.success)
    } else {
      quoteStateAtom.setError(ctx, result.failure)
    }
  }, `${name}.refresh`).pipe(withAbort())

  const prevAmountAtom = atom<bigint | undefined>(undefined)
  const reactionAction = reaction(async (ctx) => {
    refreshAction.abort(ctx, 'inputs-changed')

    const amount = ctx.spy(inputs.amountInRawAtom)
    const tokenIn = ctx.spy(inputs.tokenInAtom)
    const tokenOut = ctx.spy(inputs.tokenOutAtom)
    const taker = ctx.spy(inputs.takerAtom)
    if (inputs.modeAtom) {
      ctx.spy(inputs.modeAtom)
    }

    const prev = ctx.get(prevAmountAtom)
    prevAmountAtom(ctx, amount)

    if (
      !tokenIn ||
      !tokenOut ||
      !taker ||
      amount === undefined ||
      amount <= 0n
    ) {
      quoteStateAtom.setNotInitiated(ctx)
      return
    }

    quoteStateAtom.setLoading(ctx)

    if (prev !== undefined && prev !== amount) {
      await ctx.schedule(() => sleep(INPUT_DEBOUNCE_MS))
    }

    refreshAction(ctx)
  }, `${name}.reaction`)

  const bestVariantAtom = atom((ctx) => {
    const state = ctx.spy(quoteStateAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return undefined
    }
    return pickBestVariant(state.value)
  }, `${name}.bestVariant`)

  const transactionAtom = atom((ctx) => {
    const v = ctx.spy(bestVariantAtom)
    return v?.transaction ?? undefined
  }, `${name}.transaction`)

  const variantsAtom = atom((ctx) => {
    const state = ctx.spy(quoteStateAtom)
    return AsyncStateN.isSuccess(state) ? state.value.variants : []
  }, `${name}.variants`)

  const priceImpactBpsAtom = atom((ctx) => {
    const v = ctx.spy(bestVariantAtom)
    return v?.price_impact_bps ?? 0
  }, `${name}.priceImpactBps`)

  onConnect(quoteStateAtom, (ctx) => {
    const sub = reactionAction(ctx)
    refreshAction(ctx)
    return () => {
      refreshAction.abort(ctx, 'disconnect')
      sub.unsubscribe()
    }
  })

  return {
    quoteStateAtom,
    bestVariantAtom,
    transactionAtom,
    variantsAtom,
    priceImpactBpsAtom,
    lastLatencyMsAtom,
    refreshAction,
    reactionAction,
  }
}

export type PathfinderQuoteAtoms = ReturnType<typeof reatomPathfinderQuote>
